import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Landmark, Calculator, FileCheck, Users, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { LoanOfficerCard } from '@/components/loan-officers/LoanOfficerCard';
import { OfferLeadForm } from '@/components/forms/OfferLeadForm';
import { MortgageCalculator } from '@/components/mortgage/MortgageCalculator';
import { supabase } from '@/integrations/supabase/client';
import { gaEvents, useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  theme: string;
  favicon_url: string | null;
  tenant_id: string | null;
  canonical_base_url: string | null;
  header_font: string | null;
  company_name: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  realtor_assigned?: boolean;
}

interface TownData {
  id: string;
  name: string;
  slug: string;
}

interface RealtorData {
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url: string | null;
  title_preference?: string | null;
}

interface LoanOfficerData {
  id: string;
  first_name: string;
  last_name: string;
  photo_url: string | null;
  logo_url: string | null;
  company_name: string | null;
  city: string | null;
  state: string | null;
  email: string | null;
  phone: string | null;
  license_state: string | null;
  license_number: string | null;
  bio: string | null;
}

export default function MortgageInfoPage() {
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteData | null>(null);
  const [towns, setTowns] = useState<TownData[]>([]);
  const [areas, setAreas] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [loanOfficers, setLoanOfficers] = useState<LoanOfficerData[]>([]);
  const [displayedOfficer, setDisplayedOfficer] = useState<LoanOfficerData | null>(null);
  const [loading, setLoading] = useState(true);

  useGoogleAnalytics(site?.tenant_id || null);

  useEffect(() => {
    fetchSiteData();
  }, []);

  // Track offer page view
  useEffect(() => {
    if (site && !loading) {
      gaEvents.viewOffer('mortgage_info', '/mortgages');
    }
  }, [site, loading]);

  // Rotate loan officers evenly - select one based on rotation index
  useEffect(() => {
    if (loanOfficers.length === 0) {
      setDisplayedOfficer(null);
      return;
    }
    
    if (loanOfficers.length === 1) {
      setDisplayedOfficer(loanOfficers[0]);
      return;
    }

    // Get and increment the rotation index from localStorage
    const storageKey = `loan_officer_rotation_${site?.id || 'default'}`;
    const currentIndex = parseInt(localStorage.getItem(storageKey) || '0', 10);
    const selectedIndex = currentIndex % loanOfficers.length;
    
    setDisplayedOfficer(loanOfficers[selectedIndex]);
    
    // Increment for next view
    localStorage.setItem(storageKey, String(currentIndex + 1));
  }, [loanOfficers, site?.id]);

  const fetchSiteData = async () => {
    setLoading(true);
    try {
      const hostname = window.location.hostname;
      const isDevelopment = hostname === 'localhost' || hostname.includes('lovable.app') || hostname.includes('lovableproject.com') || hostname === '127.0.0.1';
      
      let siteData: SiteData | null = null;

      if (!isDevelopment) {
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, company_name, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww}`)
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (!siteData && isDevelopment) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: siteAdmin } = await supabase
            .from('site_admins')
            .select('site_id')
            .eq('user_id', user.id)
            .single();

          if (siteAdmin) {
            const { data } = await supabase
              .from('sites_public' as any)
              .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, company_name, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
              .eq('id', siteAdmin.site_id)
              .maybeSingle();
            siteData = data as unknown as SiteData;
          }
        }
      }

      if (!siteData) {
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, company_name, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (siteData) {
        // Redirect if feature is disabled
        if (siteData.enable_mortgage_info === false) {
          navigate('/', { replace: true });
          return;
        }
        
        setSite(siteData);

        // Fetch towns
        const { data: townsData } = await supabase
          .from('towns')
          .select('id, name, slug')
          .eq('site_id', siteData.id)
          .eq('is_published', true)
          .order('name');
        setTowns(townsData || []);
        
        // If only one town, fetch its areas for the AreaNavBar
        if (townsData && townsData.length === 1) {
          const { data: areasData } = await supabase
            .from('areas')
            .select('id, name, slug')
            .eq('town_id', townsData[0].id)
            .eq('is_published', true)
            .order('name');
          
          if (areasData) setAreas(areasData);
        }

        // Fetch realtor
        const { data: realtorData } = await supabase
          .from('realtor_profiles')
          .select('first_name, last_name, photo_url, phone, city, state, license_numbers, about_me, logo_url, title_preference, ad_image_url')
          .eq('site_id', siteData.id)
          .maybeSingle();
        setRealtor(realtorData);

        // Fetch loan officers who have coverage for any town/area on this site
        // First get all town and area IDs for this site
        const townIds = townsData?.map(t => t.id) || [];
        const areaIds: string[] = [];
        
        if (townIds.length > 0) {
          const { data: areasData } = await supabase
            .from('areas')
            .select('id')
            .eq('site_id', siteData.id)
            .eq('is_published', true);
          areasData?.forEach(a => areaIds.push(a.id));
        }

        // Find loan officers with coverage for these towns/areas
        let officerIds: string[] = [];
        if (townIds.length > 0 || areaIds.length > 0) {
          const { data: coverageData } = await supabase
            .from('loan_officer_coverage')
            .select('loan_officer_id, town_id, area_id');
          
          const matchingOfficerIds = new Set<string>();
          coverageData?.forEach(c => {
            if ((c.town_id && townIds.includes(c.town_id)) || 
                (c.area_id && areaIds.includes(c.area_id))) {
              matchingOfficerIds.add(c.loan_officer_id);
            }
          });
          officerIds = Array.from(matchingOfficerIds);
        }

        // Fetch the matching active loan officers
        if (officerIds.length > 0) {
          const { data: officersData } = await supabase
            .from('loan_officers')
            .select('id, first_name, last_name, photo_url, logo_url, company_name, city, state, email, phone, license_state, license_number, bio')
            .in('id', officerIds)
            .eq('is_active', true)
            .order('last_name');
          setLoanOfficers(officersData || []);
        } else {
          setLoanOfficers([]);
        }
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Site not found</p>
      </div>
    );
  }

  const showRealtorContent = site.realtor_assigned !== false;
  const realtorName = showRealtorContent && realtor?.first_name && realtor?.last_name
    ? `${realtor.first_name} ${realtor.last_name}`
    : null;

  const canonicalUrl = site.canonical_base_url 
    ? `${site.canonical_base_url}/mortgages`
    : null;

  const navTowns = towns.map(t => ({ id: t.id, name: t.name, slug: t.slug }));

  const mortgageTopics = [
    { icon: Calculator, title: 'Understanding Mortgage Rates', description: 'Learn how interest rates affect your monthly payment and total cost over the life of your loan.' },
    { icon: FileCheck, title: 'Pre-Approval Process', description: 'Get pre-approved before you shop so sellers know you\'re a serious buyer with financing ready.' },
    { icon: Users, title: 'Choosing the Right Lender', description: 'Compare rates, fees, and service from multiple lenders to find the best fit for your situation.' },
    { icon: Shield, title: 'Loan Types Explained', description: 'Understand the differences between conventional, FHA, VA, and jumbo loans to choose wisely.' },
    { icon: Landmark, title: 'Down Payment Options', description: 'Explore down payment assistance programs and strategies to make homeownership more accessible.' },
  ];

  const tips = [
    'Check your credit score and address any issues before applying',
    'Avoid opening new credit accounts or making large purchases before closing',
    'Ask about all fees including origination, appraisal, and closing costs',
    'Consider the total cost of the loan, not just the monthly payment',
    'Lock your rate when you find favorable terms',
  ];

  return (
    <PublicThemeProvider themeId={site.theme} headerFontId={site.header_font}>
      <div className="min-h-screen bg-background flex flex-col">
        <Helmet>
          <title>Mortgage Information & Tips | {site.name}</title>
          <meta name="description" content={`Get expert mortgage guidance for buying a home in ${site.location_served}. Learn about rates, lenders, and the pre-approval process.`} />
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
          
          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={site.name} />
          <meta property="og:title" content={`Mortgage Information & Tips | ${site.name}`} />
          <meta property="og:description" content={`Get expert mortgage guidance for buying a home in ${site.location_served}. Learn about rates, lenders, and the pre-approval process.`} />
          {/* og:url and og:image are injected by Cloudflare worker */}
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`Mortgage Information & Tips | ${site.name}`} />
          <meta name="twitter:description" content={`Get expert mortgage guidance for buying a home in ${site.location_served}.`} />
          
          {/* Additional SEO */}
          <meta name="robots" content="index, follow" />
        </Helmet>

        <Header 
          siteName={site.name} 
          faviconUrl={site.favicon_url}
          logoUrl={realtor?.logo_url}
          showAdmin={false}
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableCommunityGuide={site.enable_community_guide !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />

        <TownNavBar 
          towns={navTowns}
          currentTownSlug=""
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
          realtorFirstName={realtor?.first_name}
          themeId={site.theme}
        />
        
        {/* Area Navigation Bar - Show when only one town exists */}
        {towns.length === 1 && areas.length > 0 && (
          <AreaNavBar areas={areas} townSlug={towns[0].slug} />
        )}

        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-16 md:py-24" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)' }}>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
              <h1 className="text-3xl md:text-5xl font-display font-bold mb-4 text-white">
                Navigate Your Mortgage with Confidence
              </h1>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto text-white/90">
                Get the knowledge you need to secure the best financing for your new home in {site.location_served}.
              </p>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-12 md:py-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                {/* Left: Content */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Loan Officer Section - Show one officer (rotated) - placed first */}
                  {displayedOfficer && (
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-6">
                        Your Local Mortgage Expert
                      </h2>
                      <LoanOfficerCard 
                        officer={displayedOfficer} 
                        realtorName={realtorName}
                        realtorCompany={site.company_name}
                      />
                    </div>
                  )}

                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
                      Understanding the Mortgage Process
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Getting a mortgage doesn't have to be overwhelming. Whether you're a first-time buyer or 
                      looking to refinance, understanding the basics will help you make informed decisions and 
                      potentially save thousands over the life of your loan.
                    </p>
                    <p className="text-muted-foreground">
                      {realtorName || 'Your local real estate professional'} can connect you with trusted mortgage 
                      professionals in {site.location_served} who will walk you through every step of the process.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {mortgageTopics.map((topic, index) => (
                      <Card key={index} className="bg-muted/50">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-2 rounded-full bg-accent/20 shrink-0">
                              <topic.icon className="h-6 w-6 text-accent-foreground" />
                            </div>
                            <div>
                              <h3 className="font-semibold mb-1">{topic.title}</h3>
                              <p className="text-sm text-muted-foreground">{topic.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Tips Section */}
                  <div className="bg-secondary/30 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-accent-foreground" />
                      Tips for Choosing a Mortgage Broker
                    </h3>
                    <ul className="space-y-3">
                      {tips.map((tip, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-accent-foreground shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-3">
                      Ready to Take the Next Step?
                    </h3>
                    <p className="text-muted-foreground">
                      {realtorName || 'Your local agent'} works with reputable mortgage professionals who offer 
                      competitive rates and personalized service. Fill out the form to get connected with a 
                      lender who understands the {site.location_served} market.
                    </p>
                  </div>
                </div>

                {/* Right: Form + Realtor */}
                <div className="space-y-6">
                  <Card className="p-6 lg:p-8">
                    <h3 className="text-2xl font-display font-bold mb-2 text-center">
                      Get Mortgage Guidance
                    </h3>
                    <p className="text-muted-foreground text-center mb-6">
                      {displayedOfficer 
                        ? `Request a consultation from ${displayedOfficer.first_name} ${displayedOfficer.last_name} by submitting the form below.`
                        : 'Request a consultation with a trusted mortgage professional in your area.'}
                    </p>
                    <OfferLeadForm 
                      siteId={site.id}
                      requestType="mortgage"
                      pagePath="/mortgages"
                      submitButtonText={displayedOfficer ? `Connect Me with ${displayedOfficer.first_name}` : "Connect Me with a Lender"}
                      siteDomain={window.location.hostname}
                      loanOfficer={displayedOfficer ? {
                        name: `${displayedOfficer.first_name} ${displayedOfficer.last_name}`,
                        company: displayedOfficer.company_name,
                        email: displayedOfficer.email,
                        phone: displayedOfficer.phone,
                        license: displayedOfficer.license_number || null,
                      } : null}
                    />
                  </Card>

                  <MortgageCalculator />
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer 
          siteName={site.name} 
          locationServed={site.location_served}
          towns={navTowns}
          areas={areas}
          realtor={showRealtorContent ? realtor : null}
          enableBuyers={true}
          enableSellers={true}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />
      </div>
    </PublicThemeProvider>
  );
}
